import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sparkles, Inbox, Mail, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { auth } from '../utils/auth';
import Sidebar from '../components/Sidebar';
import WorkspaceShell from '../components/ai-workspace/WorkspaceShell';
import CaseSwitchConfirmModal from '../components/ai-workspace/CaseSwitchConfirmModal';
import CaseSelectionView from '../components/ai-workspace/CaseSelectionView';
import PreCaseThread from '../components/ai-workspace/PreCaseThread';
import { useCaseSelection } from '../hooks/useCaseSelection';
import { useTranslation } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AIWorkspacePage - Standalone AI Workspace module
 * 
 * Now supports:
 * - Regular case workspace: /lexy/ai-workspace
 * - Pre-case workspace: /lexy/ai-workspace/pre-case/:threadId
 * 
 * Features pre-case list in sidebar when no case is selected
 * Design: Minimal, Linear/ChatGPT inspired
 */
const AIWorkspacePage = () => {
  const navigate = useNavigate();
  const { threadId, caseId } = useParams(); // For pre-case routes and direct case access
  const { t } = useTranslation();
  
  // Determine if we're in pre-case mode
  const isPreCaseMode = !!threadId;
  
  // Use custom hook for case selection logic
  const {
    cases,
    selectedCase,
    recentCases,
    searchQuery,
    setSearchQuery,
    filteredCases,
    loading,
    fetchCases,
    selectCase
  } = useCaseSelection();
  
  // Pre-case threads state
  const [preCaseThreads, setPreCaseThreads] = useState([]);
  const [loadingPreCases, setLoadingPreCases] = useState(false);
  
  // Local UI state
  const [showCaseSwitchConfirm, setShowCaseSwitchConfirm] = useState(false);
  const [pendingCase, setPendingCase] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [preCasePanelCollapsed, setPreCasePanelCollapsed] = useState(true); // Collapsed by default
  const [activeSubTab, setActiveSubTab] = useState('chat');

  // Check authentication and fetch cases on mount
  useEffect(() => {
    const user = auth.getUser();
    const allowedRoles = ['LAWYER', 'ADMIN', 'TENANT_ADMIN'];
    if (!user || !allowedRoles.includes(user.role)) {
      navigate('/lexy');
      return;
    }
    fetchCases();
    fetchPreCaseThreads();
  }, [navigate, fetchCases]);
  
  // Auto-select case if caseId is provided in URL
  useEffect(() => {
    if (caseId && cases.length > 0) {
      const targetCase = cases.find(c => c.id === caseId);
      if (targetCase) {
        // Always select the case from URL, even if another case is selected
        if (!selectedCase || selectedCase.id !== caseId) {
          selectCase(targetCase);
        }
      }
    }
  }, [caseId, cases, selectedCase, selectCase]);

  const fetchPreCaseThreads = async () => {
    setLoadingPreCases(true);
    try {
      const token = auth.getToken();
      const response = await axios.get(
        `${API}/outlook/precase-threads`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreCaseThreads(response.data.threads || []);
    } catch (error) {
      console.error('Error fetching pre-case threads:', error);
    } finally {
      setLoadingPreCases(false);
    }
  };

  // Handle case switch (with confirmation if needed)
  const handleCaseSwitch = (newCase) => {
    if (!selectedCase) {
      selectCase(newCase);
      return;
    }
    
    if (selectedCase.id === newCase.id) {
      return;
    }
    
    // Different case - show confirmation
    setPendingCase(newCase);
    setShowCaseSwitchConfirm(true);
  };

  const confirmCaseSwitch = () => {
    if (pendingCase) {
      selectCase(pendingCase);
      setPendingCase(null);
    }
    setShowCaseSwitchConfirm(false);
  };

  const handleCreateNewCase = () => {
    navigate('/lexy/cases/new');
  };

  const handleNavigate = (view) => {
    const routes = {
      'overview': '/lexy/dashboard',
      'case-queue': '/lexy/dashboard?tab=case-queue',
      'cases': '/lexy/dashboard?tab=cases',
      'inbox': '/lexy/dashboard?tab=inbox',
      'calendar': '/lexy/dashboard?tab=calendar',
      'community': '/lexy/dashboard?tab=community',
      'marketplace': '/lexy/dashboard?tab=marketplace',
      'clients': '/lexy/dashboard?tab=clients',
      'time': '/lexy/dashboard?tab=time',
      'invoices': '/lexy/dashboard?tab=invoices',
      'analytics': '/lexy/dashboard?tab=analytics',
      'settings': '/lexy/dashboard?tab=settings',
      'organization': '/lexy/dashboard?tab=organization'
    };
    
    if (routes[view]) {
      navigate(routes[view]);
    } else if (view === 'ai-workspace') {
      navigate('/lexy/ai-workspace');
    } else if (view.startsWith('ai-workspace-')) {
      setActiveSubTab(view.replace('ai-workspace-', ''));
    }
  };

  // Priority helpers
  const getPriorityColor = (p) => ({
    'URGENT': 'bg-red-500',
    'HIGH': 'bg-orange-500',
    'NORMAL': 'bg-yellow-500',
    'LOW': 'bg-green-500'
  }[p] || 'bg-slate-400');

  // If in pre-case mode, render PreCaseThread
  if (isPreCaseMode) {
    return (
      <div className="flex h-screen bg-white overflow-hidden">
        <Sidebar 
          activeView="ai-workspace"
          onNavigate={handleNavigate}
          isExpanded={sidebarExpanded}
          onToggle={() => setSidebarExpanded(!sidebarExpanded)}
        />
        <div className={`flex-1 flex flex-col h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-14'}`}>
          <PreCaseThread />
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen bg-white overflow-hidden">
        <Sidebar 
          activeView="ai-workspace"
          onNavigate={handleNavigate}
          isExpanded={sidebarExpanded}
          onToggle={() => setSidebarExpanded(!sidebarExpanded)}
        />
        <div className={`flex-1 flex items-center justify-center ${sidebarExpanded ? 'md:ml-56' : 'md:ml-14'}`}>
          <div className="text-center px-4">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Laster AI Workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen bg-white overflow-hidden">
        <Sidebar 
          activeView="ai-workspace"
          onNavigate={handleNavigate}
          isExpanded={sidebarExpanded}
          onToggle={() => setSidebarExpanded(!sidebarExpanded)}
        />

        <div className={`flex-1 flex flex-col h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-14'}`}>
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            {selectedCase ? (
              <div className="h-full flex flex-col bg-white overflow-hidden">
                {/* Workspace content - CaseStatusHeader handles the header internally */}
                <WorkspaceShell 
                  caseId={selectedCase.id} 
                  caseName={selectedCase.title || selectedCase.topic}
                  activeTab={activeSubTab}
                  onSwitchCase={() => {
                    localStorage.removeItem('ai_workspace_active_case');
                    selectCase(null);
                    navigate('/lexy/ai-workspace');
                  }}
                />
              </div>
            ) : (
              <div className="h-full flex overflow-hidden">
                {/* Left sidebar - Pre-case threads (collapsible) */}
                <div className={`border-r border-gray-100 bg-white flex flex-col transition-all duration-300 flex-shrink-0 ${
                  preCasePanelCollapsed ? 'w-12' : 'w-64 sm:w-72'
                }`}>
                  {preCasePanelCollapsed ? (
                    // Collapsed view - icons only
                    <div className="flex flex-col items-center py-3 gap-2">
                      <button
                        onClick={() => setPreCasePanelCollapsed(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t('precase.newInquiries')}
                      >
                        <Inbox className="w-5 h-5 text-gray-600" />
                      </button>
                      {preCaseThreads.length > 0 && (
                        <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">
                          {preCaseThreads.length}
                        </span>
                      )}
                      <button
                        onClick={() => navigate('/lexy/dashboard?tab=inbox')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-auto"
                        title={t('precase.goToInbox')}
                      >
                        <Mail className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    // Expanded view
                    <>
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Inbox className="w-5 h-5 text-gray-600" />
                            {t('precase.newInquiries')}
                          </h2>
                          <button
                            onClick={() => setPreCasePanelCollapsed(true)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="Collapse"
                          >
                            <ChevronRight className="w-4 h-4 text-gray-400 rotate-180" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{t('precase.preCaseThreads')}</p>
                      </div>
                      
                      <div className="flex-1 overflow-y-auto">
                        {loadingPreCases ? (
                          <div className="p-4 text-center">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                          </div>
                        ) : preCaseThreads.length > 0 ? (
                          <div className="p-2 space-y-1">
                            {preCaseThreads.map(thread => (
                              <button
                                key={thread.id}
                                onClick={() => navigate(`/lexy/ai-workspace/pre-case/${thread.id}`)}
                                className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition group"
                              >
                                <div className="flex items-start gap-2">
                                  <div className={`w-2 h-2 rounded-full mt-2 ${getPriorityColor(thread.priority)}`} />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 text-sm truncate group-hover:text-indigo-600">
                                      {thread.email_data?.subject || 'Ny henvendelse'}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {thread.email_data?.from?.name || thread.email_data?.from?.email}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                                        {thread.deep_analysis?.category || 'Ukjent'}
                                      </span>
                                    </div>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition" />
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500">
                            <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">{t('precase.noNewInquiries')}</p>
                            <p className="text-xs mt-1 text-gray-400">{t('precase.useInboxToOpen')}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-3 border-t border-gray-100">
                        <button
                          onClick={() => navigate('/lexy/dashboard?tab=inbox')}
                          className="w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition flex items-center justify-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          {t('precase.goToInbox')}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Main content - Case selection */}
                <div className="flex-1 bg-gray-50 min-w-0">
                  <CaseSelectionView
                    cases={cases}
                    recentCases={recentCases}
                    filteredCases={filteredCases}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    onSelectCase={selectCase}
                    onCreateNewCase={handleCreateNewCase}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CaseSwitchConfirmModal
        isOpen={showCaseSwitchConfirm}
        pendingCase={pendingCase}
        onConfirm={confirmCaseSwitch}
        onCancel={() => {
          setShowCaseSwitchConfirm(false);
          setPendingCase(null);
        }}
      />
    </>
  );
};

export default AIWorkspacePage;
